/**
 * JoinerNode - Output merging and comparison node
 *
 * Collects outputs from Looper iterations or connected nodes and
 * provides side-by-side comparison with AI-powered merging.
 *
 * Features:
 * - Side-by-side comparison of all inputs (ComparisonView)
 * - Multi-select inputs for merging
 * - Four merge modes (MergeSelector)
 * - Merge button triggers merge operation
 * - Merged output preview
 * - Left handle for inputs, right handle for merged output
 *
 * This node is designed to work downstream of LooperNode,
 * collecting all iteration outputs for review and combination.
 */

import { memo, useState, useCallback, useEffect } from 'react';
import { Handle, Position, NodeResizer, type NodeProps, type Node } from '@xyflow/react';
import type { JoinerNodeData, MergeMode } from '@deep-agents/shared-types';
import { ComparisonView } from './ComparisonView';
import { MergeSelector } from './MergeSelector';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * JoinerNode React Flow node type
 */
type JoinerNodeType = Node<JoinerNodeData, 'joiner'>;

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * JoinerNode component for React Flow canvas
 *
 * Props come from React Flow's NodeProps with our JoinerNodeData type.
 * The node displays:
 * - Floating title input (on hover/select)
 * - ComparisonView for input display and selection
 * - MergeSelector for merge mode
 * - Merge button
 * - Merged output preview (when complete)
 */
export const JoinerNode = memo(({ id, data, selected, dragging }: NodeProps<JoinerNodeType>) => {
  // ========== Destructure data with defaults ==========
  const {
    title = 'Joiner',
    inputs = [],
    selectedIds = [],
    mergeMode = 'user-select',
    mergedOutput,
    mergeStatus = 'idle',
    mergeError,
    onTitleChange,
    onSelectionChange,
    onMergeModeChange,
    onMerge,
    boardId,
  } = data;

  // ========== Local state ==========
  const [localTitle, setLocalTitle] = useState(title);
  const [isHovered, setIsHovered] = useState(false);

  // Sync local title with prop changes
  useEffect(() => { setLocalTitle(title); }, [title]);

  // ========== Shadow elevation based on state ==========
  const getShadowStyle = () => {
    if (dragging) return 'var(--shadow-elevation-high)';
    if (selected) return 'var(--shadow-elevation-medium)';
    return 'var(--shadow-elevation-low)';
  };

  // ========== Callbacks ==========

  // Handle title change
  const handleTitleChange = useCallback((newTitle: string) => {
    setLocalTitle(newTitle);
    onTitleChange?.(newTitle);
  }, [onTitleChange]);

  // Handle selection change from ComparisonView
  const handleSelectionChange = useCallback((ids: string[]) => {
    onSelectionChange?.(ids);
  }, [onSelectionChange]);

  // Handle merge mode change from MergeSelector
  const handleMergeModeChange = useCallback((mode: MergeMode) => {
    onMergeModeChange?.(mode);
  }, [onMergeModeChange]);

  // Handle merge button click
  const handleMerge = useCallback(async () => {
    if (mergeMode === 'ai-merge' && selectedIds.length > 0) {
      // AI merge - will call chat API via onMerge callback
      onMerge?.();
    } else {
      // Simple merge modes (concatenate, merge-last, user-select)
      // handled by parent via onMerge callback
      onMerge?.();
    }
  }, [mergeMode, selectedIds, onMerge]);

  // ========== UI state ==========
  const showControls = isHovered || selected;

  // Can merge if:
  // - Has selections (for user-select and ai-merge modes)
  // - OR mode is concatenate/merge-last (uses all inputs)
  const canMerge = selectedIds.length > 0 || mergeMode === 'concatenate' || mergeMode === 'merge-last';

  return (
    <div
      className="relative transition-all duration-300"
      style={{ padding: '0 8px' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* ========== Connection Handles ========== */}

      {/* Left handle - receives inputs from Looper or other nodes */}
      <Handle
        type="target"
        position={Position.Left}
        id="inputs"
        className="!w-4 !h-4 !bg-green-400 !border-2 !border-white"
        style={{ top: '50%' }}
        title="Connect outputs to merge"
      />

      {/* Right handle - outputs merged result to downstream nodes */}
      <Handle
        type="source"
        position={Position.Right}
        id="merged"
        className="!w-4 !h-4 !bg-indigo-500 !border-2 !border-white"
        style={{ top: '50%' }}
        title="Merged output"
      />

      {/* ========== Floating Header (title input) ========== */}
      <div
        className={`absolute bottom-full left-0 right-0 pb-2 transition-opacity duration-200 ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      >
        <div className="flex items-center gap-2 px-1 nodrag nopan">
          <input
            type="text"
            value={localTitle}
            onChange={(e) => handleTitleChange(e.target.value)}
            placeholder="Untitled Joiner"
            className="px-2.5 py-1 text-[12px] font-semibold bg-gradient-to-r from-green-50 to-teal-50 rounded-lg border border-green-100/50 outline-none focus:border-green-300 focus:ring-1 focus:ring-green-200 placeholder:text-green-300 text-green-700"
          />
        </div>
      </div>

      {/* ========== Main Card ========== */}
      <div
        className="relative bg-white rounded-2xl overflow-visible border border-neutral-200/60 transition-all duration-300"
        style={{
          boxShadow: getShadowStyle(),
          minWidth: 400,
          minHeight: 350,
        }}
      >
        {/* Node resizer (visible when selected) */}
        <NodeResizer
          minWidth={400}
          minHeight={350}
          isVisible={selected}
          lineClassName="border-green-500"
          handleClassName="bg-green-500 border-2 border-white rounded"
        />

        <div className="px-5 pt-5 pb-4">
          {/* ========== Comparison View (input grid) ========== */}
          <div className="mb-4 nodrag nopan">
            <ComparisonView
              inputs={inputs}
              selectedIds={selectedIds}
              onSelectionChange={handleSelectionChange}
            />
          </div>

          {/* ========== Merge Mode Selector ========== */}
          <div className="mb-4 nodrag nopan">
            <MergeSelector
              mode={mergeMode}
              onChange={handleMergeModeChange}
              disabled={mergeStatus === 'merging'}
            />
          </div>

          {/* ========== Merge Button ========== */}
          <div className="nodrag nopan">
            <button
              onClick={handleMerge}
              disabled={!canMerge || mergeStatus === 'merging'}
              className={`
                w-full flex items-center justify-center gap-2 px-4 py-2.5
                text-[14px] font-semibold rounded-lg transition-all
                ${!canMerge || mergeStatus === 'merging'
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-green-500 to-teal-500 text-white hover:from-green-600 hover:to-teal-600 shadow-md hover:shadow-lg'
                }
              `}
            >
              {mergeStatus === 'merging' ? (
                <>
                  {/* Spinner icon */}
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Merging...
                </>
              ) : (
                <>
                  {/* Merge icon */}
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  Merge Selected
                </>
              )}
            </button>
          </div>

          {/* ========== Merged Output Preview ========== */}
          {mergedOutput !== undefined && mergedOutput !== null && mergeStatus === 'complete' && (
            <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
              <div className="text-[10px] font-semibold text-green-600 uppercase mb-1">
                Merged Output
              </div>
              <pre className="text-xs text-gray-700 font-mono max-h-20 overflow-auto">
                {typeof mergedOutput === 'string'
                  ? mergedOutput
                  : JSON.stringify(mergedOutput, null, 2)
                }
              </pre>
            </div>
          )}

          {/* ========== Error Display ========== */}
          {mergeError && (
            <div className="mt-4 p-3 bg-red-50 rounded-lg border border-red-200">
              <div className="text-xs text-red-600">{mergeError}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

// Display name for React DevTools
JoinerNode.displayName = 'JoinerNode';
